import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { OSDock } from "@/components/ui/os-dock";
import { OSStatusBar } from "@/components/ui/os-status-bar";
import { OSMenuBar } from "@/components/ui/os-menu-bar";
import { BackgroundVariants } from "@/components/ui/background-variants";
import { api, KPIData } from "@/lib/api";
import TodayTasks from "@/components/ui/today-tasks";
import { TaskManagerProvider } from "@/contexts/TaskManager";
import { WindowContainer } from "@/components/ui/window-container";
import { WidgetContainer } from "@/components/ui/widget-container";
import { usePreferences } from "@/contexts/PreferencesContext";
import { VersionBadge } from "@/components/ui/version-badge";
import { useAppsManager } from "@/contexts/AppsManager";
import { useWindowManager } from "@/contexts/WindowManager";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { PWAInstallPrompt } from "@/components/ui/pwa-install-prompt";


function DashboardContent() {
  const [kpis, setKpis] = useState<KPIData | null>(null);
  const [loading, setLoading] = useState(true);
  const { backgroundType } = usePreferences();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openApp, apps } = useAppsManager();
  usePushNotifications(); // Auto-subscribe to push notifications
  const { openWindow, isWindowOpen } = useWindowManager();

  // Handle ?joinMeeting= deep link — open SoundStage with that meeting
  useEffect(() => {
    const joinMeetingId = searchParams.get('joinMeeting');
    if (joinMeetingId && !loading) {
      sessionStorage.setItem('soundstage:joinMeeting', joinMeetingId);
      const ssApp = apps.find(a => a.component_type === 'SoundStageWindow');
      if (ssApp) openApp(ssApp.id);
      searchParams.delete('joinMeeting');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading, apps, openApp, setSearchParams]);

  // Expose a global function for chat → SoundStage integration
  const openWindowRef = useRef(openWindow);
  const appsRef = useRef(apps);
  openWindowRef.current = openWindow;
  appsRef.current = apps;

  useEffect(() => {
    (window as any).__openSoundStageWithMeeting = (meetingId: string) => {
      sessionStorage.setItem('soundstage:joinMeeting', meetingId);

      // Find SoundStage app ID dynamically
      const ssApp = appsRef.current.find((a: any) =>
        a.component_type === 'SoundStageWindow' ||
        a.name?.toLowerCase().includes('soundstage')
      );
      const windowId = ssApp?.id || 'soundstage';

      // Open/focus the SoundStage window (openWindow handles both cases)
      const SoundStageWindow = React.lazy(() => import('@/components/windows/SoundStageWindow'));
      openWindowRef.current(
        windowId,
        'SoundStage',
        <React.Suspense fallback={<div />}><SoundStageWindow /></React.Suspense>
      );

      // Dispatch event for SoundStageMain to load the meeting
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('soundstage:join', { detail: { meetingId } }));
      }, 800);
    };
    return () => { delete (window as any).__openSoundStageWithMeeting; };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const kpiData = await api.getKPIs();
        setKpis(kpiData);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center relative">
        <div className="absolute inset-0 -z-10">
          <BackgroundVariants type={backgroundType} />
        </div>
        <div className="relative z-10 text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading Open Insurance OS Workbench...</p>
        </div>
      </div>
    );
  }

  return (
    <TaskManagerProvider>
      <div className="min-h-screen w-full relative overflow-hidden">
        {/* Dynamic Background System */}
        <BackgroundVariants type={backgroundType} />
        
        {/* macOS Menu Bar */}
        <OSMenuBar 
          kpis={kpis}
        />
        
        
        {/* Today's Tasks Widget - Top Right */}
        <div className="fixed top-12 right-6 z-40 pointer-events-auto mr-20">
          <TodayTasks />
        </div>
        
        {/* Main Content Area - Clean and minimal */}
        <div className="relative z-10 min-h-screen pointer-events-none">
          {/* Empty main area - content lives in windows and widgets */}
        </div>
        
        {/* Window Container for OS-style windows */}
        <WindowContainer />
        
        {/* Widget Container for floating widgets */}
        <WidgetContainer />
        
        {/* OS-Style Dock */}
        <OSDock kpis={kpis} />
        
        {/* Version Badge - Clickable changelog viewer */}
        <VersionBadge />

        {/* PWA Install Prompt */}
        <PWAInstallPrompt />
      </div>
    </TaskManagerProvider>
  );
}

export default function Dashboard() {
  return <DashboardContent />;
}